import { Button, Col, Form, message, Input, Row } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import styles from './index.module.scss';
import { Messages } from '../../../constant/messages';
import { useIndicator } from '../../../hook/use_indicator';
import { checkPassword } from '../../../utils/check_utils';
import { ApiError, useApi } from '../../../api/api';

/**
 * パスワード変更画面
 * @constructor
 */
export const PasswordChangePage : React.FC = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const indicator = useIndicator();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>(null);
  const [confirmError, setConfirmError] = useState<string>(null);
  const api = useApi();
  const navigator = useNavigate();

  const onClick = useCallback(() => {
    indicator.show();
    api.path('/api/accounts/me/password').put({ password }).then((resp) => {
      if (resp.isError) {
        const { status } = resp;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
        return;
      }
      messageApi.success(Messages.EXECUTE_CHANGE_PASSWORD_SUCCESS);
    }).catch((e) => {
      window.alert(Messages.ERR_DIALOG_TIMEOUT);
    })
      .finally(() => {
        indicator.hide();
      });
  }, [password]);

  const validatePassword = useCallback(() => {
    if (password.length === 0) {
      setPasswordError(null);
      return;
    }
    if (checkPassword(password)) {
      setPasswordError('');
      return;
    }
    setPasswordError(Messages.ERR_CHK_INVALID_PASSWORD);
  }, [password]);

  const validateConfirmPassword = useCallback(() => {
    setConfirmError(() => {
      if (confirmPassword.length === 0) {
        return null;
      }
      if (password === confirmPassword) {
        return '';
      }
      return Messages.ERR_CHK_MISMATCH_PASSWORD;
    });
  }, [password, confirmPassword]);

  const disableButton = useMemo(() => {
    if (passwordError === null || confirmError === null) {
      return true;
    }
    if (password.length === 0 || confirmPassword.length === 0) {
      return true;
    }
    if (passwordError.length > 0) {
      return true;
    }
    if (confirmError.length > 0) {
      return true;
    }
    return false;
  }, [confirmError, passwordError, password, confirmPassword]);

  return (
    <div>
      {contextHolder}
      <h1>パスワード変更画面</h1>
      <div className={styles.section}>
          <Form>
              <Row>
                  <Col span={8}>
                      <Input.Password
                          key={'password'}
                          size={'large'}
                          placeholder={'新しいパスワード'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onBlur={() => {
                            validatePassword();
                            validateConfirmPassword();
                          }}
                      />
                  </Col>
              </Row>
              <Row className={styles.error_row}>
                  <Col className={styles.space}>
                      <div className={styles.error}>
                          {passwordError}
                      </div>
                  </Col>
              </Row>
              <Row>
                  <Col span={8}>
                      <Input.Password
                          key={'password'}
                          size={'large'}
                          placeholder={'新しいパスワード（確認用）'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          onBlur={validateConfirmPassword}
                      />
                  </Col>
              </Row>
              <Row className={styles.error_row}>
                  <Col className={styles.space}>
                      <div className={styles.error}>
                          {confirmError}
                      </div>
                  </Col>
              </Row>
              <Form.Item>
                <Row>
                  <Col>
                    <div style={{ textAlign: 'left', fontSize: '12px', color: '#888' }}>
                      パスワードは、7文字以上で英字（A〜Z、a〜z）および数字（0〜9）の両方を含めて設定してください。<br/>
                      記号は !@#$%^&*()_+-=[]{'{}'}| が利用可能です。
                    </div>
                  </Col>
                </Row>
              </Form.Item>
              <Form.Item style={{ paddingTop: '20px' }}>
                  <Row>
                      <Col>
                          <Button type={'primary'} disabled={disableButton} onClick={onClick}>変更</Button>
                      </Col>
                  </Row>
              </Form.Item>
          </Form>
      </div>
    </div>
  );
};
